/**
 * i18n à fallback visible pour Christian.
 *
 * Convention :
 * - Clé : `<préfixe>.<zone>.<élément>`
 *     · `cms.new.*` → CMS avancé (éditeur + rendu)
 *     · `bo.*`      → chrome back-office / ancien CMS
 * - Fallback : `{label français} ({clé complète})` tant que la clé n'est pas
 *   remplie dans le catalogue backend (`app`). Une fois la clé remplie, seul le
 *   libellé traduit s'affiche.
 *
 * Zones usuelles : sidebar, editor, categories, blocks, fields, options, tabs,
 * style, pickers, preview, pages, seo, public, contactForm…
 */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n, { I18N_DEFAULT_NS } from "@/i18n";

/** Clés du CMS avancé (sous-ensemble typé). */
export type CmsNewKey = `cms.new.${string}`;
/** Clés du chrome back-office / ancien CMS. */
export type BoKey = `bo.${string}`;
/** N'importe quelle clé à fallback visible. */
export type VisibleKey = CmsNewKey | BoKey | (string & {});

/** `label (clé)` — le marqueur visible tant que la clé n'est pas traduite. */
export function visibleFallback(key: string, label: string): string {
  return `${label} (${key})`;
}

/**
 * Lit la valeur UNIQUEMENT dans le namespace BDD (`translation`).
 *
 * Important : `t(key, { fallbackNS: false })` / `exists(..., { fallbackNS: false })`
 * ne suffisent PAS — i18next continue de résoudre via le `fallbackNS` global
 * (`print-multiples`, `auth`). On lit donc le bundle catalogue à la main.
 */
function catalogValue(key: string): string | undefined {
  const langs = [
    i18n.resolvedLanguage,
    i18n.language,
    ...(i18n.languages ?? []),
  ].filter((lng): lng is string => !!lng);

  const seen = new Set<string>();
  for (const lng of langs) {
    if (seen.has(lng)) continue;
    seen.add(lng);

    const viaPath = i18n.getResource(lng, I18N_DEFAULT_NS, key);
    if (typeof viaPath === "string" && viaPath.trim() !== "") return viaPath;

    const bundle = i18n.getResourceBundle(lng, I18N_DEFAULT_NS) as
      | Record<string, unknown>
      | undefined;
    const flat = bundle?.[key];
    if (typeof flat === "string" && flat.trim() !== "") return flat;
  }
  return undefined;
}

/**
 * Traduction à fallback visible — hors composant React (config Puck, constantes,
 * fonctions `render`). Réévaluée à chaque appel : ne PAS stocker le résultat au
 * niveau module (il figerait sur le fallback avant chargement du bundle).
 */
export function visibleT(key: VisibleKey, label: string): string {
  return catalogValue(key) ?? visibleFallback(key, label);
}

/** Hook — traduction à fallback visible dans un composant React. */
export function useVisibleT() {
  // `i18n` dans les deps : re-crée le callback quand le bundle BDD est chargé /
  // rafraîchi (`bindI18nStore: "added"` côté init) ou que la langue change.
  const { i18n: i18nInst } = useTranslation();
  return useCallback(
    (key: VisibleKey, label: string) =>
      catalogValue(key) ?? visibleFallback(key, label),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- i18nInst identity / language
    [i18nInst, i18nInst.language, i18nInst.resolvedLanguage],
  );
}

// --- Alias rétro-compatibles CMS avancé (clé typée `cms.new.*`) -----------------

/** @deprecated interne — préférer `visibleFallback`. */
export function cmsNewFallback(key: CmsNewKey, label: string): string {
  return visibleFallback(key, label);
}

/** Traduction CMS avancé — pour config / constantes hors composant React. */
export function cmsNew(key: CmsNewKey, label: string): string {
  return visibleT(key, label);
}

/** Hook pour composants React Puck. */
export function useCmsNew(): (key: CmsNewKey, label: string) => string {
  return useVisibleT();
}

/**
 * Compteur incrémenté quand le bundle i18n charge ou que la langue change.
 * À utiliser comme dépendance de `useMemo` pour les configs Puck construites
 * avec `cmsNew()` (sinon les libellés restent figés sur le fallback initial).
 */
export function useTranslationBundleRevision(): number {
  const { i18n } = useTranslation();
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    const bump = () => setRevision((n) => n + 1);
    i18n.on("languageChanged", bump);
    i18n.on("loaded", bump);
    return () => {
      i18n.off("languageChanged", bump);
      i18n.off("loaded", bump);
    };
  }, [i18n]);

  return revision;
}
